// 推廣活動（Lead Campaign）後台 — 對標食肆優惠三 TAB 模式：
// TAB1 活動管理（CRUD/toggle/落地 link 複製）｜TAB2 領取名單（郵寄流轉＋label 列印/CSV）｜TAB3 成效報表（漏斗＋郵寄＋CAC/LTV）｜TAB5 玩法 Demo（全閉環 block diagram）
// Label 規格：A4 14 上（99.1×38.1mm，2×7）——設計為「信封上的邀請卡」：
//   收件地址（功能）＋收件人專屬推薦 QR（閉環：朋友掃碼落單＝收件人賺回贈）＋影相分享誘因。
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Megaphone, Download, Printer, RefreshCw, Plus, Edit2, Trash2, Copy, Users, BarChart3, Eye, PlayCircle} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import AdminLayout from '../components/AdminLayout';
import GodfatherDemo from '../components/GodfatherDemo';
import { API_BASE, apiFetch } from '../lib/api';
import RichTextEditor from '../components/RichTextEditor';
import MediaPicker from '../components/MediaPicker';

interface GalleryItem { url: string; caption?: string }

interface Campaign {
  id: number;
  key: string;
  name: string;
  headline: string;
  subline: string;
  benefits: string[];
  detailsHtml?: string;
  gallery?: Array<{ url: string; caption?: string }>;
  shortCode: string;
  giftProductId: number | null;
  adSpendCents: number | null;
  maxClaims: number | null;
  deliveryMode: string;
  pickupShops: { name: string; address: string; merchantId?: number; quota?: number }[];
  isActive: number;
  sortOrder: number;
  claimCount: number;
}
interface Claim {
  id: number;
  phone: string;
  name: string;
  address: string;
  region: string;
  referral_code: string | null;
  ref_token?: string | null;
  status: string;
  sent_at?: number | null;
  picked_up_at?: number | null;
  campaign_id: number | null;
  created_referrer: number;
  created_at: number;
  task_commitment: number;
  task_platforms: string | null;
  task_due_at: number | null;
  task_status: string;
  post_url: string | null;
}
interface StatsCommerce {
  buyers: number;
  orders: number;
  revenueCents: number;
  revenuePerBuyerCents: number;
  cacCents: number | null;
  adSpendCents: number;
  leadToBuyerRate: string;
}
interface Stats {
  funnel: { views: number; formStarts: number; submitsOk: number; submitsDupPhone: number; submitsBlocked: number; claims: number; newMembers: number };
  mailing: Record<string, number>;
  commerce?: StatsCommerce;
}

const STATUS_LABELS: Record<string, string> = { waitlist: '後補', claimed: '待寄出', sent: '已寄出' };
const emptyForm = { key: '', name: '', headline: '', subline: '', benefitsText: '', sortOrder: '0', isActive: true, giftProductId: '' as string | number, adSpendHkd: '', maxClaims: '', deliveryMode: 'mail' };
const emptyShopRow = { name: '', address: '', merchantId: undefined as number | undefined, quota: '' as string };

// Label 預覽樣本（效果圖用，非真實客戶）
const PREVIEW_SAMPLES = [
  { name: '陳大文', address: '九龍旺角彌敦道 123 號幸福大廈 12 樓 A 室', qr: 'https://hkspicyvalley.com/?r=DEMO01' },
  { name: 'Alex Li', address: '新界沙田第一城 12 座 5 樓 B 室', qr: 'https://hkspicyvalley.com/?r=DEMO02' },
];

const AdminGodfather: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'campaigns' | 'claims' | 'stats' | 'tasks' | 'demo' | 'wanla'>('campaigns');
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [pendingAudit, setPendingAudit] = useState<Array<{ code: string; name: string; address: string; region: string; phone: string; ageMinutes: number; campaignKey: string }>>([]);
  const [pendingMsg, setPendingMsg] = useState('');
  const fetchPendingAudit = async () => {
    setPendingMsg('');
    const res = await apiFetch('/api/public/admin/godfather/pending-audit');
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { setPendingMsg('檢查失敗'); return; }
    setPendingAudit((data as any).data?.pending || []);
    if (((data as any).data?.pending || []).length === 0) setPendingMsg('現時冇卡住嘅登記 ✓');
  };

  const extendPending = async (all: boolean, code?: string) => {
    const res = await apiFetch('/api/public/admin/godfather/pending-audit', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(all ? { all: true, hours: 72 } : { code, hours: 72, remove: true }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { setPendingMsg('操作失敗'); return; }
    setPendingMsg(`已完成：${(data as any).data?.action || 'OK'}（${(data as any).data?.touched || 0} 條）`);
    if (all) await fetchPendingAudit();
  };
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [claims, setClaims] = useState<Claim[]>([]);
  const [stats, setStats] = useState<Record<number, Stats>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showShopPicker, setShowShopPicker] = useState(false);
  const [pickupShopsEdit, setPickupShopsEdit] = useState<Array<{ name: string; address: string; merchantId?: number; quota?: string }>>([]);
  const openShopPicker = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/public/admin/merchants`, { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      const raw = data.data ?? data;
      const list = (Array.isArray(raw) ? raw : []) as Array<Record<string, unknown>>;
      const inList = new Set(pickupShopsEdit.map((r) => r.name.trim()).filter(Boolean));
      const usable = list
        .filter((m) => String(m.status || '') === 'active' && String(m.address || '').trim()
          && Number(m.activeOfferCount || 0) > 0 && !inList.has(String(m.name)))
        .map((m) => ({ id: Number(m.id), name: String(m.name), address: String(m.address).trim(), offers: Number(m.activeOfferCount || 0) }));
      setShopPickerList(usable);
      setShopPicked(new Set());
      setShowShopPicker(true);
    } catch {
      setError('載入商家名單失敗');
    }
  };
  const [shopPickerList, setShopPickerList] = useState<{ id: number; name: string; address: string; offers: number }[]>([]);
  const [shopPicked, setShopPicked] = useState<Set<number>>(new Set());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [detailsHtml, setDetailsHtml] = useState('');
  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const [showGalleryPicker, setShowGalleryPicker] = useState(false);
  const [taskEvents, setTaskEvents] = useState<Array<Record<string, unknown>>>([]);
  const [taskCampaignFilter, setTaskCampaignFilter] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);
  const [claimsCampaignFilter, setClaimsCampaignFilter] = useState<number | 'all'>('all');
  // 名單狀態篩選（可見即印：篩選結果＝表格＝CSV＝Label 列印）
  const [giftStatusFilter, setGiftStatusFilter] = useState<'all' | 'claimed' | 'sent' | 'waitlist'>('all');
  const [taskStatusFilter, setTaskStatusFilter] = useState<'all' | 'none' | 'submitted' | 'completed'>('all');
  const [products, setProducts] = useState<{ id: number; name: string }[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    fetchCampaigns();
  }, [navigate]);

  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(''), 2500); return () => clearTimeout(t); }
  }, [success]);

  // 切到名單/報表 TAB 時補抓數據（mount 時 tab=campaigns，claims 不會在首載抓取）
  useEffect(() => {
    if ((tab === 'claims' || tab === 'stats' || tab === 'tasks') && campaigns.length) {
      fetchClaims(campaigns);
      if (tab === 'tasks') fetchTaskEvents(taskCampaignFilter === '' ? campaigns[0]?.id ?? '' : taskCampaignFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const authHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/public/admin/lead-campaigns');
      if (res.status === 401) { localStorage.removeItem('admin_token'); navigate('/admin'); return; }
      const data = await res.json();
      const rows = Array.isArray(data) ? data : data.data || [];
      setCampaigns(rows);
      // 贈品掛鉤產品選單（靜默失敗不影響主功能）
      apiFetch('/api/public/admin/products').then((r) => r.json()).then((pd) => {
        const list = Array.isArray(pd) ? pd : pd.data || [];
        setProducts(list.map((p: any) => ({ id: p.id, name: p.name })));
      }).catch(() => {});
      if (rows.length && (tab === 'claims' || tab === 'stats' || tab === 'tasks')) await fetchClaims(rows);
    } catch (e: any) { setError(e.message || '載入失敗'); }
    finally { setLoading(false); }
  };

  const fetchClaims = async (list?: Campaign[]) => {
    const res = await apiFetch('/api/public/admin/godfather/claims');
    const data = await res.json();
    setClaims(Array.isArray(data) ? data : []);
    // 成效報表數據按活動抓取
    const ids = (list || campaigns).map((c) => c.id);
    const entries = await Promise.all(ids.map(async (id) => {
      const r = await apiFetch(`/api/public/admin/lead-campaigns/${id}/stats`);
      const d = await r.json().catch(() => null);
      return [id, d && (d.funnel ? d : d.data)] as [number, Stats];
    }));
    setStats(Object.fromEntries(entries));
  };

  const fetchTaskEvents = async (cid: number | '') => {
    const qs = cid !== '' ? `?campaignId=${cid}` : '';
    const res = await apiFetch(`/api/public/admin/godfather/task-events${qs}`);
    const data = await res.json().catch(() => null);
    setTaskEvents(Array.isArray(data) ? data : data?.data || []);
  };
  const openCreate = () => { setForm({ ...emptyForm }); setDetailsHtml(''); setGallery([]); setEditingId(null); setShowForm(true); };
  const openEdit = (c: Campaign) => {
    setForm({
      key: c.key, name: c.name, headline: c.headline || '', subline: c.subline || '',
      benefitsText: (c.benefits || []).join('\n'),
      sortOrder: String(c.sortOrder || 0), isActive: !!c.isActive,
      giftProductId: c.giftProductId || '', adSpendHkd: c.adSpendCents !== null ? (c.adSpendCents / 100).toString() : '',
      maxClaims: c.maxClaims !== null && c.maxClaims !== undefined ? String(c.maxClaims) : '',
      deliveryMode: c.deliveryMode === 'pickup' ? 'pickup' : 'mail',
      pickupShopsEdit: (c.pickupShops || []).map((x) => ({ name: x.name || '', address: x.address || '', merchantId: x.merchantId, quota: x.quota !== null && x.quota !== undefined ? String(x.quota) : '' })),
    });
    setDetailsHtml(c.detailsHtml || ''); setGallery(c.gallery || []);
    setEditingId(c.id);
    setShowForm(true);
  };

  // 總名額＝各店名額之和（自動計算）；pickup 模式下 max_claims 由總和寫出
  const totalQuota = pickupShopsEdit.reduce((sum, r) => {
    const q = /^\d+$/.test((r.quota || '').trim()) ? parseInt(r.quota, 10) : 0;
    return sum + q;
  }, 0);

  const save = async () => {
    if (!form.name.trim() || (!editingId && !form.key.trim())) { setError('請填寫 Key 與名稱'); return; }
    setSaving(true); setError('');
    try {
      const pickupRows = form.deliveryMode === 'pickup'
        ? pickupShopsEdit.map((r) => {
            const quotaNum = /^\d+$/.test((r.quota || '').trim()) ? Math.max(0, parseInt(r.quota, 10)) : null;
            return {
              name: r.name.trim(),
              address: r.address.trim(),
              ...(r.merchantId ? { merchantId: r.merchantId } : {}),
              ...(quotaNum !== null ? { quota: quotaNum } : {}),
            };
          }).filter((x) => x.name)
        : [];
      const payload = {
        key: form.key.trim().toLowerCase(), name: form.name.trim(), headline: form.headline.trim(),
        subline: form.subline.trim(), benefits: form.benefitsText.split('\n').map((s) => s.trim()).filter(Boolean),
        detailsHtml, gallery,
        sortOrder: Number(form.sortOrder || 0), isActive: form.isActive,
        giftProductId: form.giftProductId ? Number(form.giftProductId) : null,
        adSpendCents: form.adSpendHkd !== '' ? Math.round((parseFloat(form.adSpendHkd) || 0) * 100) : null,
        maxClaims: form.deliveryMode === 'pickup'
          ? (totalQuota > 0 ? totalQuota : null)
          : (form.maxClaims !== '' ? Math.max(1, Math.round(Number(form.maxClaims))) : null),
        deliveryMode: form.deliveryMode,
        pickupShops: pickupRows,
      };
      const res = await fetch(editingId ? `${API_BASE}/api/public/admin/lead-campaigns/${editingId}` : `${API_BASE}/api/public/admin/lead-campaigns`, {
        method: editingId ? 'PUT' : 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '儲存失敗');
      setShowForm(false);
      setSuccess(editingId ? '活動已更新' : '活動已建立');
      await fetchCampaigns();
    } catch (e: any) { setError(e.message || '儲存失敗'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (c: Campaign) => {
    const res = await fetch(`${API_BASE}/api/public/admin/lead-campaigns/${c.id}/toggle`, { method: 'POST', headers: authHeaders() });
    if (res.ok) await fetchCampaigns(); else setError('切換失敗');
  };

  const remove = async (c: Campaign) => {
    if (!confirm(`確定刪除活動「${c.name}」？`)) return;
    const res = await fetch(`${API_BASE}/api/public/admin/lead-campaigns/${c.id}`, { method: 'DELETE', headers: authHeaders() });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { setError(data.error || '刪除失敗'); return; }
    setSuccess('已刪除');
    await fetchCampaigns();
  };

  const updateClaimStatus = async (id: number, status: string) => {
    const res = await apiFetch(`/api/public/admin/godfather/claims/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { setError((data as any).error || '更新失敗'); return; }
    const n = (data as any).notify;
    if (n && n.sent === false) setError(`已更新為「${STATUS_LABELS[status]}」，但客戶 WhatsApp 通知發送失敗（${n.error || '未知原因'}）——請檢查發送裝置在線後補發`);
    else if (n && n.sent) setSuccess(`已更新為「${STATUS_LABELS[status]}」✓ 客戶 WhatsApp 通知已發送`);
    else setSuccess(`已更新為「${STATUS_LABELS[status]}」`);
    await fetchClaims();
  };

  const campaignMode = (campaignId: number | null): string => {
    if (!campaignId) return '';
    const camp = campaigns.find((x) => x.id === campaignId);
    return camp ? String(camp.deliveryMode || '') : '';
  };

  const promoteWaitlist = async (id: number) => {
    const res = await apiFetch(`/api/public/admin/godfather/claims/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'claimed' }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { setError('轉正失敗'); return; }
    const n = (data as any).notify;
    if (n && n.sent === false) setError(`已轉正選，但 WhatsApp 好消息發送失敗（${n.error || '未知原因'}）——請檢查發送裝置`);
    else setSuccess('已轉正選 ✓ WhatsApp 好消息已發送');
    await fetchClaims();
  };

  const deleteClaim = async (id: number, name: string) => {
    const res = await apiFetch(`/api/public/admin/godfather/claims/${id}`, { method: 'DELETE' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { setError((data as any).error || '刪除失敗'); return; }
    setSuccess(`已刪除「${name}」嘅領取記錄`);
    setConfirmDeleteId(null);
    await fetchClaims();
  };

  const verifyTask = async (id: number, currentUrl?: string) => {
    const postUrl = (window.prompt('分享帖連結（客人貼出嘅原文連結）：', currentUrl || '') ?? '').trim();
    if (!postUrl) { setError('核實需要分享帖連結——請客人補交或貼上已回報嘅連結'); return; }
    const res = await apiFetch(`/api/public/admin/godfather/claims/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taskStatus: 'completed', postUrl }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { setError('核實失敗'); return; }
    const n = (data as any).notify;
    if (n && n.sent === false) setError(`分享已核實，但感謝訊息發送失敗（${n.error || '未知原因'}）`);
    else setSuccess('分享已核實 ✓ 感謝訊息已發送');
    await fetchClaims();
  };

  const copyLandingLink = (c: Campaign) => {
    const url = c.shortCode
      ? `https://hkspicyvalley.com/gf/${c.shortCode}`
      : `https://hkspicyvalley.com/godfather/${c.key}`;
    navigator.clipboard?.writeText(url).then(() => setSuccess('推廣短連結已複製 ✓')).catch(() => {});
  };

  const exportLabelCsv = () => {
    const list = filteredClaims;
    const rows = [['活動', '姓名', '地址行', '地區', '電話', '狀態', '推薦碼', '新會員', '領取時間']];
    const campName = (id: number | null) => campaigns.find((c) => c.id === id)?.name || '';
    for (const c of list) {
      rows.push([
        campName(c.campaign_id), c.name,
        c.address.replace(/\n/g, ' ').replace(/,/g, '，'),
        c.region === 'mainland' ? '內地' : '香港', c.phone,
        STATUS_LABELS[c.status] || c.status, c.referral_code || '',
        c.created_referrer ? '是' : '舊會員',
        new Date(c.created_at * 1000).toLocaleDateString('zh-HK'),
      ]);
    }
    const csv = '\uFEFF' + rows.map((r) => r.map((v) => `"${String(v)}"`).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lead-campaign-mailing-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredClaims = useMemo(() => {
    let list = claimsCampaignFilter === 'all' ? claims : claims.filter((c) => c.campaign_id === claimsCampaignFilter);
    if (giftStatusFilter !== 'all') list = list.filter((c) => c.status === giftStatusFilter);
    if (taskStatusFilter !== 'all') list = list.filter((c) => (c.task_status || 'none') === taskStatusFilter);
    return list;
  }, [claims, claimsCampaignFilter, giftStatusFilter, taskStatusFilter]);

  // A4 14 上列印（99.1×38.1mm，2 欄×7 行）——列印目前篩選的名單
  const labelSheets = useMemo(() => {
    const chunks: Claim[][] = [];
    for (let i = 0; i < filteredClaims.length; i += 14) chunks.push(filteredClaims.slice(i, i + 14));
    return chunks;
  }, [filteredClaims]);

  const printLabels = () => {
    if (!filteredClaims.length) { setError('沒有可列印的記錄'); return; }
    setTimeout(() => window.print(), 250);
  };

  const inputCls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white text-sm';
  const btn = 'px-4 py-2 rounded-lg font-medium transition';
  const tabBtn = (t: string, label: string, icon?: React.ReactNode) => (
    <button onClick={() => setTab(t as any)}
      className={`px-4 py-2 font-medium text-sm rounded-lg transition flex items-center gap-1.5 ${tab === t ? 'bg-red-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
      {icon}{label}
    </button>
  );

  return (
    <AdminLayout currentPage="godfather">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <Megaphone className="w-6 h-6 text-red-600" /> 推廣活動
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">群轉個人獲客活動——後台建活動、貼連結、看漏斗（對標食肆優惠模式）</p>
          </div>
          <button onClick={openCreate} className={`${btn} bg-red-600 text-white hover:bg-red-700 flex items-center gap-2`}>
            <Plus size={18} /> 新增活動
          </button>
        </div>

        {success && <div className="bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 p-3 rounded-lg text-sm">{success}</div>}
        {error && !showForm && <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 p-3 rounded-lg text-sm">{error}</div>}

        <div className="flex gap-2">
          {tabBtn('campaigns', '活動管理', <Megaphone size={16} />)}
          {tabBtn('claims', '領取名單', <Users size={16} />)}
          {tabBtn('stats', '成效報表', <BarChart3 size={16} />)}
          {tabBtn('tasks', '任務跟蹤', <BarChart3 size={16} />)}
          {tabBtn('demo', '玩法 Demo', <PlayCircle size={16} />)}
        </div>

        {/* TAB 5：玩法 Demo（block diagram——示範整個流程俾人睇；唔使等 loading） */}
        {tab === 'demo' && <GodfatherDemo />}

        {loading ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">載入中…</div>
        ) : (
          <>
            {/* TAB 1：活動管理 */}
            {tab === 'campaigns' && (campaigns.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-10 text-center text-gray-500 dark:text-gray-400">
                還沒有活動。點「新增活動」建立你的第一場群轉個人推廣。
              </div>
            ) : (
              <div className="space-y-3">
                {campaigns.map((c) => (
                  <div key={c.id} className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-bold ${c.isActive ? 'text-gray-800 dark:text-white' : 'text-gray-400 dark:text-gray-500 line-through'}`}>{c.name}</span>
                          <span className="text-xs font-mono text-gray-400">{c.key}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${c.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                            {c.isActive ? '進行中' : '已結束'}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">{c.claimCount} 人領取</span>
                        </div>
                        {c.headline && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{c.headline} — {c.subline}</p>}
                        <div className="flex items-center gap-3 mt-2 text-xs">
                          <button onClick={() => copyLandingLink(c)} className="text-blue-600 hover:underline flex items-center gap-1 dark:text-blue-400">
                            <Copy size={12} /> {c.shortCode ? `/gf/${c.shortCode}` : `/godfather/${c.key}`}
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => toggleActive(c)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${c.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                          {c.isActive ? '進行中' : '已結束'}
                        </button>
                        <button onClick={() => openEdit(c)} className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400"><Edit2 size={18} /></button>
                        <button onClick={() => remove(c)} className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400"><Trash2 size={18} /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}

            {/* TAB 2：領取名單 */}
            {tab === 'claims' && (
              <>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <select value={String(claimsCampaignFilter)} onChange={(e) => setClaimsCampaignFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))} className={`${inputCls} w-auto`}>
                      <option value="all">全部活動</option>
                      {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <select value={giftStatusFilter} onChange={(e) => setGiftStatusFilter(e.target.value as any)} className={`${inputCls} w-auto`} title="禮品狀態篩選（作用於表格／CSV／列印）">
                      <option value="all">禮品：全部</option>
                      <option value="claimed">禮品：已領取·待寄出</option>
                      <option value="sent">禮品：已寄出</option>
                      <option value="waitlist">禮品：後補</option>
                    </select>
                    <select value={taskStatusFilter} onChange={(e) => setTaskStatusFilter(e.target.value as any)} className={`${inputCls} w-auto`} title="分享任務篩選（作用於表格／CSV／列印）">
                      <option value="all">分享：全部</option>
                      <option value="none">分享：待分享</option>
                      <option value="submitted">分享：已回報·待核實</option>
                      <option value="completed">分享：已完成</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={fetchCampaigns} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 text-sm flex items-center gap-1.5"><RefreshCw size={14} /> 刷新</button>
                    <button onClick={() => setPreviewOpen(true)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 text-sm flex items-center gap-1.5"><Eye size={14} /> 效果圖</button>
                    <button onClick={exportLabelCsv} disabled={!filteredClaims.length} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 text-sm flex items-center gap-1.5 disabled:opacity-50"><Download size={14} /> CSV</button>
                    <button onClick={printLabels} disabled={!filteredClaims.length} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"><Printer size={14} /> 列印 Label（A4 14上 · {filteredClaims.length} 筆）</button>
                  </div>
                </div>
                {(giftStatusFilter !== 'all' || taskStatusFilter !== 'all') && (
                  <p className="text-xs text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/50 rounded-lg px-3 py-1.5">
                    🖨 可見即印——列印範圍：禮品＝{giftStatusFilter === 'all' ? '全部' : giftStatusFilter === 'claimed' ? '已領取·待寄出' : giftStatusFilter === 'sent' ? '已寄出' : '後補'}
                    ｜分享＝{taskStatusFilter === 'all' ? '全部' : taskStatusFilter === 'none' ? '待分享' : taskStatusFilter === 'submitted' ? '已回報·待核實' : '已完成'}
                    ｜共 {filteredClaims.length} 筆（{Math.ceil(filteredClaims.length / 14)} 張 A4）
                  </p>
                )}
                {!filteredClaims.length ? (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-10 text-center text-gray-500 dark:text-gray-400">暫時未有領取記錄。</div>
                ) : (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-stone-100 dark:bg-gray-700 text-left">
                        <tr>
                          <th className="px-4 py-3 font-medium">收件人</th>
                          <th className="px-4 py-3 font-medium">WhatsApp</th>
                          <th className="px-4 py-3 font-medium">地址</th>
                          <th className="px-4 py-3 font-medium">會員</th>
                          <th className="px-4 py-3 font-medium">領取日</th>
                          <th className="px-4 py-3 font-medium">郵寄狀態</th>
                          <th className="px-4 py-3 font-medium">分享任務</th>
                          <th className="px-4 py-3 font-medium">操作</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {filteredClaims.map((c) => (
                          <tr key={c.id} className="hover:bg-stone-50 dark:hover:bg-gray-700/50">
                            <td className="px-4 py-3 font-medium text-gray-800 dark:text-white">{c.name}</td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-400 font-mono text-xs">{c.phone}</td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-400 max-w-xs">{c.address}</td>
                            <td className="px-4 py-3">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${c.created_referrer ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                                {c.created_referrer ? '新會員' : '舊會員'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{new Date(c.created_at * 1000).toLocaleDateString('zh-HK')}</td>
                            <td className="px-4 py-3">
                              {/* 固定次序按鈕：後補→待寄出→已寄出（杜絕跳級；後端 side effects 各自綁定）
                                  到店模式：claimed＝待到店領取（核銷由店員掃碼完成，後台不出按鈕） */}
                              <div className="flex flex-col items-start gap-1">
                                {c.status === 'waitlist' && (
                                  <button onClick={() => promoteWaitlist(c.id)} className="px-3 py-1 rounded-lg text-xs font-bold bg-teal-500 text-white hover:bg-teal-600 transition-colors">轉正選＋通知 →</button>
                                )}
                                {c.status === 'claimed' && campaignMode(c.campaign_id) === 'pickup' && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">⏳ 待到店領取</span>
                                )}
                                {c.status === 'claimed' && campaignMode(c.campaign_id) !== 'pickup' && (
                                  <button onClick={() => updateClaimStatus(c.id, 'sent')} className="px-3 py-1 rounded-lg text-xs font-bold bg-indigo-500 text-white hover:bg-indigo-600 transition-colors">標記已寄出 →</button>
                                )}
                                {c.status === 'sent' && campaignMode(c.campaign_id) === 'pickup' && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                                    ✅ 已到店領取{c.picked_up_at ? `（${new Date(c.picked_up_at * 1000).toLocaleDateString('zh-HK')}）` : ''}
                                  </span>
                                )}
                                {c.status === 'sent' && campaignMode(c.campaign_id) !== 'pickup' && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                                    ✅ 已寄出{c.sent_at ? `（${new Date(c.sent_at * 1000).toLocaleDateString('zh-HK')}）` : ''}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {c.task_status === 'completed' ? (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">✅ 已完成分享</span>
                                  {c.post_url && (
                                    <a href={c.post_url} target="_blank" rel="noreferrer" title={c.post_url}
                                      className="text-[11px] px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50">🔗 分享帖</a>
                                  )}
                                </div>
                              ) : c.task_status === 'submitted' ? (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">📥 已回報·待核實</span>
                                  {c.post_url && (
                                    <a href={c.post_url} target="_blank" rel="noreferrer" title={c.post_url}
                                      className="text-[11px] px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 font-bold">🔗 看分享帖</a>
                                  )}
                                  <button onClick={() => verifyTask(c.id, c.post_url || undefined)} className="text-[11px] px-2 py-0.5 rounded bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-300">✓ 核實</button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs text-gray-400">⏳ 待分享</span>
                                  {c.status !== 'waitlist' && (
                                    <button onClick={() => verifyTask(c.id)} className="text-[11px] px-2 py-0.5 rounded bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-300" title="手動核實（需提供分享帖連結）">✓ 核實分享</button>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {confirmDeleteId === c.id ? (
                                <button onClick={() => deleteClaim(c.id, c.name)} onBlur={() => setConfirmDeleteId(null)} autoFocus
                                  className="text-[11px] px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700 font-medium">確認刪除？</button>
                              ) : (
                                <button onClick={() => { setConfirmDeleteId(c.id); setTimeout(() => setConfirmDeleteId((cur) => (cur === c.id ? null : cur)), 4000); }}
                                  className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition" title="刪除記錄">
                                  <Trash2 size={15} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {/* TAB 3：成效報表 */}
            {tab === 'stats' && (
              <div className="space-y-6">
                {campaigns.filter((c) => stats[c.id]).map((c) => {
                  const s = stats[c.id];
                  const f = s.funnel;
                  const rate = (a: number, b: number) => (b > 0 ? `${((a / b) * 100).toFixed(1)}%` : '—');
                  return (
                    <div key={c.id} className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold text-gray-800 dark:text-white">{c.name} <span className="text-xs font-mono text-gray-400">{c.key}</span></h3>
                      </div>
                      {/* 漏斗 */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        {[
                          { label: '落地頁瀏覽', value: f.views },
                          { label: '開始填表', value: f.formStarts, sub: rate(f.formStarts, f.views) },
                          { label: '提交成功', value: f.submitsOk, sub: rate(f.submitsOk, f.views) },
                          { label: '新會員', value: f.newMembers, sub: `佔提交 ${rate(f.newMembers, f.submitsOk)}` },
                        ].map((k) => (
                          <div key={k.label} className="bg-stone-50 dark:bg-gray-700/50 rounded-xl p-3">
                            <p className="text-xs text-gray-500 dark:text-gray-400">{k.label}</p>
                            <p className="text-2xl font-bold text-stone-800 dark:text-white mt-0.5">{k.value}</p>
                            {k.sub && <p className="text-xs text-red-600 mt-0.5">{k.sub}</p>}
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
                        <div className="text-gray-500 dark:text-gray-400">重複號碼（已領過）：{f.submitsDupPhone}</div>
                        <div className="text-gray-500 dark:text-gray-400">同網絡被擋：{f.submitsBlocked}</div>
                        <div className="text-gray-500 dark:text-gray-400">總領取記錄：{f.claims}</div>
                        <div className="text-gray-500 dark:text-gray-400">舊會員補領：{f.claims - f.newMembers}</div>
                      </div>
                      {/* 郵寄進度 */}
                      <div className="flex gap-2 flex-wrap mb-4">
                        {Object.entries(STATUS_LABELS).map(([st, label]) => (
                          <span key={st} className="text-xs px-3 py-1 rounded-full bg-stone-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                            {label}：<b>{s.mailing[st] || 0}</b>
                          </span>
                        ))}
                      </div>
                      {/* 商業成效（ERP 維度：leads→買家→營收→CAC） */}
                      {s.commerce && (
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                          <p className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-2">💰 商業成效（該批會員至今）</p>
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            {[
                              { label: '轉化買家', value: String(s.commerce.buyers), sub: `領取→買家 ${s.commerce.leadToBuyerRate}` },
                              { label: '貢獻訂單', value: String(s.commerce.orders), sub: '' },
                              { label: '已確認營收', value: `HK$${(s.commerce.revenueCents / 100).toFixed(0)}`, sub: '' },
                              { label: '人均消費 LTV', value: `HK$${(s.commerce.revenuePerBuyerCents / 100).toFixed(0)}`, sub: '每位買家' },
                              { label: 'CAC', value: s.commerce.cacCents !== null ? `HK$${(s.commerce.cacCents / 100).toFixed(0)}` : '—', sub: s.commerce.adSpendCents > 0 ? `投放 HK$${(s.commerce.adSpendCents / 100).toFixed(0)}` : '未填投放成本' },
                            ].map((k) => (
                              <div key={k.label} className="bg-stone-50 dark:bg-gray-700/50 rounded-xl p-3">
                                <p className="text-xs text-gray-500 dark:text-gray-400">{k.label}</p>
                                <p className="text-xl font-bold text-stone-800 dark:text-white mt-0.5">{k.value}</p>
                                {k.sub && <p className="text-xs text-gray-400 mt-0.5">{k.sub}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {!campaigns.length && <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-10 text-center text-gray-500 dark:text-gray-400">先建立活動，才有成效數據。</div>}
              </div>
            )}
          </>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-8 px-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">{editingId ? '編輯活動' : '新增活動'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">✕</button>
            </div>
            {error && <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 p-3 rounded-lg text-sm">{error}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">活動 Key *</label>
                <input value={form.key} disabled={!!editingId} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="例如：fb-q3-2026" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">活動名稱 *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="例如：Q3 FB 群友禮遇" className={inputCls} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">落地頁主標</label>
              <input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="FB 群友專屬禮遇" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">副標</label>
              <input value={form.subline} onChange={(e) => setForm({ ...form, subline: e.target.value })} placeholder="免費辣粉試吃裝 直接郵寄到你屋企…" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">賣點（每行一個，最多 3 行）</label>
              <textarea value={form.benefitsText} onChange={(e) => setForm({ ...form, benefitsText: e.target.value })} rows={3}
                placeholder={'免費試吃裝郵寄到家\n首單專屬折扣碼\n自動成為辣谷之友'} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">活動詳情（豐富內容，支援排版／emoji／連結）</label>
              <RichTextEditor value={detailsHtml} onChange={setDetailsHtml} placeholder="例如：✅ 任務要求／💡 溫馨提示／🔶 條款（會顯示在賣點下方）" minHeight={200} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">活動相片（可選，最多 12 張，前台直向展示＋點擊放大）</label>
                <button type="button" onClick={() => setShowGalleryPicker(true)} className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-bold hover:bg-red-700">＋ 加入相片</button>
              </div>
              {gallery.length === 0 ? (
                <p className="text-xs text-gray-400">還沒有相片——點「＋ 加入相片」從媒體庫選擇或直接拖放上傳。</p>
              ) : (
                <div className="space-y-2">
                  {gallery.map((g, i) => (
                    <div key={i} className="flex items-center gap-2 bg-stone-50 dark:bg-gray-900/40 rounded-lg p-2">
                      <img src={g.url} alt={g.caption || ''} className="w-16 h-12 object-cover rounded border border-stone-200 dark:border-gray-600" />
                      <input value={g.caption || ''} onChange={(e) => setGallery(gallery.map((x, xi) => xi === i ? { ...x, caption: e.target.value } : x))} placeholder="圖說（可選）" className="flex-1 min-w-0 px-2 py-1.5 border border-stone-200 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-800 dark:text-white" />
                      <div className="flex flex-col gap-0.5">
                        <button type="button" disabled={i === 0} onClick={() => { const g2 = [...gallery]; [g2[i - 1], g2[i]] = [g2[i], g2[i - 1]]; setGallery(g2); }} className="text-[10px] px-1 rounded hover:bg-stone-200 dark:hover:bg-gray-700 disabled:opacity-30 dark:text-gray-300">▲</button>
                        <button type="button" disabled={i === gallery.length - 1} onClick={() => { const g2 = [...gallery]; [g2[i + 1], g2[i]] = [g2[i], g2[i + 1]]; setGallery(g2); }} className="text-[10px] px-1 rounded hover:bg-stone-200 dark:hover:bg-gray-700 disabled:opacity-30 dark:text-gray-300">▼</button>
                      </div>
                      <button type="button" onClick={() => setGallery(gallery.filter((_, xi) => xi !== i))} className="text-red-500 hover:text-red-700 text-xs px-1" title="移除">✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">排序</label>
                <input type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">贈品掛鉤產品<span className="text-xs text-gray-400 ml-1">（寄出時自動扣庫存）</span></label>
                <select value={String(form.giftProductId || '')} onChange={(e) => setForm({ ...form, giftProductId: e.target.value })} className={inputCls}>
                  <option value="">（不掛鉤）</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">發放模式</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
                    <input type="radio" checked={form.deliveryMode === 'mail'} onChange={() => setForm({ ...form, deliveryMode: 'mail' })} className="accent-red-600" /> 📮 郵寄
                  </label>
                  <label className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
                    <input type="radio" checked={form.deliveryMode === 'pickup'} onChange={() => setForm({ ...form, deliveryMode: 'pickup' })} className="accent-red-600" /> 🏪 到店自選兌換
                  </label>
                </div>
              </div>
              {form.deliveryMode === 'pickup' && (
                <div>
                  <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">兌換門店<span className="text-xs text-gray-400 ml-1">（客人登記時揀；名額滿咗會自動顯示已滿）</span></label>
                    <button type="button" onClick={openShopPicker}
                      className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-300">
                      ＋ 從商家帶入
                    </button>
                  </div>

                  {/* 門店表格編輯器：一店一行（商家 ID 內部記錄唔顯示） */}
                  {pickupShopsEdit.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-4 text-center text-xs text-gray-400">
                      未有門店——撳「＋ 從商家帶入」勾選（只列有進行中優惠嘅商家），或者「＋ 手動加一間」。
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {pickupShopsEdit.map((r, i) => (
                        <div key={i} className="flex items-start gap-2 bg-stone-50 dark:bg-gray-900/40 rounded-lg p-2">
                          <div className="flex-1 min-w-0 space-y-1.5">
                            <input
                              value={r.name}
                              onChange={(e) => setPickupShopsEdit(pickupShopsEdit.map((x, xi) => xi === i ? { ...x, name: e.target.value } : x))}
                              placeholder="門店名稱 *（例：華星冰室（尖沙咀））"
                              className="w-full px-2.5 py-1.5 border border-stone-200 dark:border-gray-600 rounded-lg text-sm font-bold bg-white dark:bg-gray-800 text-gray-800 dark:text-white"
                            />
                            <input
                              value={r.address}
                              onChange={(e) => setPickupShopsEdit(pickupShopsEdit.map((x, xi) => xi === i ? { ...x, address: e.target.value } : x))}
                              placeholder="門店地址 *"
                              className="w-full px-2.5 py-1.5 border border-stone-200 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
                            />
                            {r.merchantId && <p className="text-[10px] text-gray-400">🏪 已關聯商家（核銷碼／優惠自動匹配）</p>}
                          </div>
                          <div className="flex flex-col items-center gap-1 shrink-0">
                            <label className="text-[10px] text-gray-400">名額</label>
                            <input
                              value={r.quota}
                              onChange={(e) => setPickupShopsEdit(pickupShopsEdit.map((x, xi) => xi === i ? { ...x, quota: e.target.value.replace(/\D/g, '') } : x))}
                              inputMode="numeric"
                              placeholder="不限"
                              className="w-16 px-2 py-1.5 border border-stone-200 dark:border-gray-600 rounded-lg text-sm text-center bg-white dark:bg-gray-800 text-gray-800 dark:text-white"
                            />
                          </div>
                          <button type="button" onClick={() => setPickupShopsEdit(pickupShopsEdit.filter((_, xi) => xi !== i))}
                            className="text-red-400 hover:text-red-600 text-sm px-1 self-center" title="移除">✕</button>
                        </div>
                      ))}
                      {totalQuota > 0 && (
                        <p className="text-xs font-bold text-stone-600 dark:text-gray-300">🎁 禮品總名額：{totalQuota}（＝各店名額之和，自動列後補機制照用）</p>
                      )}
                      <button type="button" onClick={() => setPickupShopsEdit([...pickupShopsEdit, { ...emptyShopRow }])}
                        className="text-xs px-2 py-1 rounded border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-400">
                        ＋ 手動加一間
                      </button>
                    </div>
                  )}
                </div>
              )}
              {form.deliveryMode === 'pickup' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">禮品名額上限<span className="text-xs text-gray-400 ml-1">（到店模式＝自動等於各店名額總和{totalQuota > 0 ? `：${totalQuota}` : ''}）</span></label>
                  <input type="number" min="1" value={totalQuota > 0 ? String(totalQuota) : form.maxClaims} disabled={totalQuota > 0}
                    onChange={(e) => setForm({ ...form, maxClaims: e.target.value })} placeholder="例如 100" className={inputCls + (totalQuota > 0 ? ' opacity-60' : '')} />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">禮品名額上限<span className="text-xs text-gray-400 ml-1">（超出＝自動列後補；留空＝不限）</span></label>
                  <input type="number" min="1" value={form.maxClaims} onChange={(e) => setForm({ ...form, maxClaims: e.target.value })} placeholder="例如 100" className={inputCls} />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">禮品名額上限<span className="text-xs text-gray-400 ml-1">（超出＝自動列後補；留空＝不限）</span></label>
                <input type="number" min="1" value={form.maxClaims} onChange={(e) => setForm({ ...form, maxClaims: e.target.value })} placeholder="例如 100" className={inputCls} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} className="w-4 h-4 accent-red-600" />
              進行中（落地頁可領取）
            </label>
            <div className="flex justify-end gap-2 border-t border-gray-200 dark:border-gray-700 pt-4">
              <button onClick={() => setShowForm(false)} className={`${btn} border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300`}>取消</button>
              <button onClick={save} disabled={saving} className={`${btn} bg-red-600 text-white hover:bg-red-700 disabled:opacity-60`}>{saving ? '儲存中…' : '儲存'}</button>
            </div>
          </div>
        </div>
      )}

      {/* 從商家帶入兌換門店——checklist 勾選（可揀多間；有地址嘅 active 商家先出現） */}
      {showShopPicker && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6 max-h-[80vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">從商家帶入兌換門店</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">勾選要帶入嘅門店（分店＝獨立一行）；已喺清單入面嘅會自動剔走。</p>
            {shopPickerList.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">未有可帶入嘅商家——請先喺「協作平台 → 商家」登記並填寫門店地址。</p>
            ) : (
              <div className="space-y-1.5 mb-4">
                {shopPickerList.map((m) => (
                  <label key={m.id} className="flex items-start gap-2 p-2 rounded-lg hover:bg-stone-50 dark:hover:bg-gray-700/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={shopPicked.has(m.id)}
                      onChange={(e) => {
                        const next = new Set(shopPicked);
                        if (e.target.checked) next.add(m.id); else next.delete(m.id);
                        setShopPicked(next);
                      }}
                      className="mt-0.5 w-4 h-4 accent-red-600"
                    />
                    <span className="text-sm">
                      <span className="font-bold text-gray-800 dark:text-white">{m.name}</span>
                      <span className="ml-1 text-xs text-red-600 dark:text-red-400">·{m.offers} 個進行中優惠</span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">{m.address}</span>
                    </span>
                  </label>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2 border-t border-gray-200 dark:border-gray-700 pt-4">
              <button onClick={() => setShowShopPicker(false)} className={`${btn} border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300`}>取消</button>
              <button
                onClick={() => {
                  const picked = shopPickerList.filter((m) => shopPicked.has(m.id));
                  const existing = new Set(pickupShopsEdit.map((r) => r.name.trim()).filter(Boolean));
                  const additions = picked
                    .filter((m) => !existing.has(m.name))
                    .map((m) => ({ name: m.name, address: m.address, merchantId: m.id, quota: '' }));
                  if (additions.length) setPickupShopsEdit([...pickupShopsEdit, ...additions]);
                  setShopPicked(new Set());
                  setShowShopPicker(false);
                }}
                disabled={shopPicked.size === 0}
                className={`${btn} bg-red-600 text-white hover:bg-red-700 disabled:opacity-50`}
              >
                帶入已選（{shopPicked.size}）
              </button>
            </div>
          </div>
        </div>
      )}
        {showGalleryPicker && (
          <MediaPicker
            onSelect={(url) => {
              setGallery((g) => g.length >= 12 ? g : [...g, { url }]);
              setShowGalleryPicker(false);
            }}
            onClose={() => setShowGalleryPicker(false)}
          />
        )}

      {/* 任務跟蹤 modal-less inline view */}
      {tab === 'tasks' && (
        <div className="space-y-4">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-sm font-bold text-amber-800 dark:text-amber-200">⏳ 卡住登記（填咗表但未完成 WhatsApp 驗證；30 分鐘後過期）</div>
              <div className="flex gap-2">
                <button onClick={fetchPendingAudit} className="px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700 text-xs font-medium">檢查</button>
                {pendingAudit.length > 0 && (
                  <button onClick={() => extendPending(false)} className="px-3 py-1.5 rounded-lg bg-white dark:bg-gray-700 border border-amber-400 text-amber-700 dark:text-amber-200 text-xs font-medium">全部延長 72 小時</button>
                )}
              </div>
            </div>
            {pendingAudit.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {pendingAudit.map((p) => (
                  <div key={p.code} className="flex items-center justify-between gap-2 text-xs bg-white/70 dark:bg-gray-800/60 rounded-lg px-3 py-2">
                    <span className="text-gray-800 dark:text-gray-100 font-medium">{p.name}</span>
                    <span className="font-mono text-gray-700 dark:text-gray-200">{p.phone || '—'}</span>
                    <span className="text-gray-500 dark:text-gray-400 flex-1 truncate">{p.address}</span>
                    <span className="text-gray-400 whitespace-nowrap">{p.ageMinutes} 分鐘前</span>
                    <button onClick={() => extendPending(true, p.code)} className="text-red-500 hover:text-red-700" title="刪除此記錄">✕</button>
                  </div>
                ))}
              </div>
            )}
            {pendingMsg && <div className="mt-2 text-xs text-amber-700 dark:text-amber-300">{pendingMsg}</div>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={taskCampaignFilter === '' ? (campaigns[0]?.id ?? '') : taskCampaignFilter}
              onChange={(e) => { const v = e.target.value === '' ? '' : Number(e.target.value); setTaskCampaignFilter(v); fetchTaskEvents(v); }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white">
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <span className="text-xs text-gray-400">顯示該活動全部谷友的任務時間線（發送過的每一步＋收到的回報）</span>
          </div>
          {claims.filter((c) => taskCampaignFilter === '' || c.campaign_id === taskCampaignFilter).length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400">此活動暫無谷友登記</div>
          ) : (
            <div className="space-y-3">
              {claims.filter((c) => taskCampaignFilter === '' || c.campaign_id === taskCampaignFilter).map((c) => {
                const pipeline = c.task_status === 'completed' ? ['⏳ 待分享', '📥 已回報', '✅ 已核實']
                  : c.task_status === 'submitted' ? ['⏳ 待分享', '📥 已回報', '◻️ 待核實']
                  : ['⏳ 待分享', '◻️ ——', '◻️ ——'];
                const step = c.task_status === 'completed' ? 3 : c.task_status === 'submitted' ? 2 : 1;
                const evs = taskEvents.filter((e) => e.claim_id === c.id);
                return (
                  <div key={c.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="font-bold text-gray-800 dark:text-gray-100">{c.name} <span className="font-mono text-xs text-gray-400">{c.phone}</span></div>
                      <div className="flex items-center gap-1.5">
                        {pipeline.map((label, i) => (
                          <span key={i} className={`text-xs px-2 py-0.5 rounded-full ${i < step ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'}`}>{label}</span>
                        ))}
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
                      {c.task_platforms && <span>分享平台：{c.task_platforms.split(',').join('、')}</span>}
                      {c.post_url && <a href={c.post_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline dark:text-blue-400">· 查看回報連結</a>}
                    </div>
                    {evs.length > 0 && (
                      <details className="mt-2">
                        <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer select-none">時間線（{evs.length} 步）</summary>
                        <div className="mt-2 space-y-1 border-l-2 border-stone-200 dark:border-gray-600 pl-3">
                          {evs.map((e, i) => (
                            <div key={i} className="text-xs text-gray-600 dark:text-gray-300">
                              <span className="text-gray-400 font-mono">{new Date((e.created_at as number) * 1000).toLocaleString('zh-HK')}</span> — {(e.event_type as string) === 'link_received' ? '📥 谷友回報分享連結' : (e.event_type as string) === 'completed' ? '✅ 分享已核實' : (e.event_type as string) === 'sent_notice' ? '📦 寄出通知' : String(e.event_type)}：{(e.content_snippet as string) || ''}
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                    {c.task_status === 'submitted' && (
                      <button onClick={() => verifyTask(c.id, c.post_url || undefined)} className="mt-3 text-xs px-3 py-1.5 rounded-lg bg-green-600 text-white font-bold hover:bg-green-700">核實完成（發感謝訊息）</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══ A4 14上 Label 列印版面（螢幕隱藏，列印獨佔）══ */}
      <style>{`
        .print-root { display: none; }
        .lc-left { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }
        .lc-brand { font-weight: 900; color: #d9381e; white-space: nowrap; }
        .lc-cert { font-weight: 900; color: #111; }
        .lc-name { font-weight: 900; color: #111; }
        .lc-addr { color: #222; line-height: 1.3; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
        .lc-cta { margin-top: auto; color: #d9381e; font-weight: 700; line-height: 1.25; }
        .lc-right { display: flex; flex-direction: column; align-items: center; justify-content: center; border-left: 1px dashed #bbb; }
        .lc-scan { font-weight: 700; color: #333; text-align: center; line-height: 1.25; }
        /* 螢幕預覽尺寸（px） */
        .label-cell { box-sizing: border-box; width: 375px; height: 144px; padding: 9px 12px; display: flex; gap: 10px; overflow: hidden; background: #fff; }
        .lc-brand { font-size: 13px; letter-spacing: 0.5px; }
        .lc-brand small { font-size: 7.5px; font-weight: 700; color: #555; letter-spacing: 1px; margin-left: 5px; }
        .lc-cert { font-size: 15px; margin-top: 3px; }
        .lc-name { font-size: 17px; margin-top: 3px; line-height: 1.15; }
        .lc-addr { font-size: 11px; margin-top: 2px; }
        .lc-cta { font-size: 9px; }
        .lc-right { flex: 0 0 122px; gap: 3px; padding-left: 7px; }
        .lc-scan { font-size: 8px; }
        @media print {
          @page { size: A4 portrait; margin: 0; }
          body * { visibility: hidden; }
          .print-root, .print-root * { visibility: visible; }
          .print-root { display: block !important; position: absolute; left: 0; top: 0; }
          .label-sheet { width: 210mm; height: 296.5mm; padding: 13.3mm 5.9mm; display: grid; grid-template-columns: repeat(2, 99.1mm); grid-auto-rows: 38.1mm; box-sizing: border-box; }
          .label-sheet:not(:first-child) { break-before: page; }
          .label-cell { width: 99.1mm; height: 38.1mm; padding: 2.2mm 3mm; gap: 2.5mm; }
          .lc-brand { font-size: 9pt; letter-spacing: 0.3mm; }
          .lc-brand small { font-size: 5.5pt; letter-spacing: 0.5mm; margin-left: 1.5mm; }
          .lc-cert { font-size: 10.5pt; margin-top: 0.8mm; }
          .lc-name { font-size: 12.5pt; margin-top: 1mm; }
          .lc-addr { font-size: 8.5pt; margin-top: 0.7mm; }
          .lc-cta { font-size: 6.8pt; }
          .lc-right { flex: 0 0 30mm; gap: 0.8mm; padding-left: 1.8mm; }
          .lc-scan { font-size: 5.6pt; }
        }
      `}</style>
      <div className="print-root" aria-hidden="true">
        {labelSheets.map((sheet, si) => (
          <div className="label-sheet" key={si}>
            {sheet.map((c) => (
              <div className="label-cell" key={c.id}>
                <div className="lc-left">
                  <div className="lc-brand">香港辣谷<small>· 辣到滴汗俱樂部</small></div>
                  <div className="lc-cert">🌶️ 正式辣腳認證 ✓</div>
                  <div className="lc-name">{c.name}</div>
                  <div className="lc-addr">{c.address}</div>
                  <div className="lc-cta">📸 開箱記得影相——你嘅朋友會流口水</div>
                </div>
                <div className="lc-right">
                  <QRCodeSVG
                    value={c.ref_token ? `https://hkspicyvalley.com/ref/d/${c.ref_token}` : c.referral_code ? `https://hkspicyvalley.com/?r=${String(c.referral_code).replace(/^R-/, '')}` : 'https://hkspicyvalley.com/store'}
                    size={76}
                    level="M"
                  />
                  <div className="lc-scan">辣腳福利！掃呢度<br />辣椒好物同隱藏折扣等緊你</div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Label 螢幕預覽 modal（效果圖） */}
      {previewOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setPreviewOpen(false)}>
          <div className="bg-stone-100 rounded-2xl shadow-2xl max-w-3xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-lg text-stone-900">Label 效果圖（A4 14上 · 99.1×38.1mm）</h3>
                <p className="text-xs text-stone-500 mt-0.5">實際列印比例參考 · 黑白打印機同樣有效</p>
              </div>
              <button onClick={() => setPreviewOpen(false)} className="text-stone-400 hover:text-stone-600 text-xl">✕</button>
            </div>
            <div className="space-y-4">
              {PREVIEW_SAMPLES.map((s, i) => (
                <div key={i} className="flex items-end gap-3">
                  <div className="label-cell flex-shrink-0" style={{ transform: 'scale(1.12)', transformOrigin: 'left center' }}>
                    <div className="lc-left">
                      <div className="lc-brand">香港辣谷<small>· 辣到滴汗俱樂部</small></div>
                      <div className="lc-cert">🌶️ 正式辣腳認證 ✓</div>
                      <div className="lc-name">{s.name}</div>
                      <div className="lc-addr">{s.address}</div>
                      <div className="lc-cta">📸 開箱記得影相——你嘅朋友會流口水</div>
                    </div>
                    <div className="lc-right">
                      <QRCodeSVG value={s.qr} size={76} level="M" />
                      <div className="lc-scan">辣腳福利！掃呢度<br />辣椒好物同隱藏折扣等緊你 🌶️</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 bg-white rounded-xl p-4 text-xs text-stone-600 space-y-1 border border-stone-200">
              <p className="font-bold text-stone-800">設計邏輯（點解會被影相分享）：</p>
              <p>① 「正式辣腳認證」＝身份徽章——收件人曬相曬的是<strong>自己嘅身份</strong>，唔係廣告</p>
              <p>② 「開箱記得影相」＝把分享動作寫進開箱儀式；朋友看照片時自然讀到文案</p>
              <p>③ QR＝收件人<strong>自己嘅推薦碼</strong>——朋友掃碼落單，收件人賺回贈：照片流去邊，增長閉環就去到邊</p>
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={() => setPreviewOpen(false)} className={`${btn} bg-stone-800 text-white`}>關閉</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminGodfather;
